import React, { useState, useEffect, useCallback } from 'react';
import { SpaceBetween, Button, Box } from '@cloudscape-design/components';
import { useHistory } from 'react-router-dom';
import FundingRequestTable from '../components/FundingRequestTable';
import { s3Service } from '../services/s3Service';
import { getFundRequestDetailsUrl } from '../utils/urlHelper';
import { useAuth } from '../hooks/useAuth';

const DEFAULT_SHOW_ALL_DAYS = 30;

const FundingRequestTracker = () => {
  const [fundingRequests, setFundingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [daysFilter, setDaysFilter] = useState(null);
  const history = useHistory();
  const { user, loading: authLoading } = useAuth();

  const userAlias = authLoading ? undefined : (user?.email ? user.email.split('@')[0] : null);

  const handleViewRequest = (requestId) => {
    history.push(getFundRequestDetailsUrl(requestId));
  };

  const loadFundingRequests = useCallback(async (isShowAll, days) => {
    if (userAlias === null) {
      setFundingRequests([]);
      return;
    }
    setLoading(true);
    try {
      const data = isShowAll
        ? await s3Service.getFundingRequests(null, days)
        : await s3Service.getFundingRequests(userAlias);
      setFundingRequests(data);
    } catch (error) {
      console.error('Error loading funding requests:', error);
      setFundingRequests([]);
    } finally {
      setLoading(false);
    }
  }, [userAlias]);

  // Fetch on mount and when userAlias changes
  useEffect(() => {
    if (userAlias !== undefined) {
      loadFundingRequests(showAll, daysFilter);
    }
  }, [userAlias]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleShowAll = () => {
    const nextShowAll = !showAll;
    const nextDays = nextShowAll ? DEFAULT_SHOW_ALL_DAYS : null;
    setShowAll(nextShowAll);
    setDaysFilter(nextDays);
    loadFundingRequests(nextShowAll, nextDays);
  };

  const handleDaysFilterChange = (newDays) => {
    setDaysFilter(newDays);
    loadFundingRequests(showAll, newDays);
  };

  return (
    <SpaceBetween size="l">
      <Box float="right">
        <Button
          variant="normal"
          iconName={showAll ? 'user-profile' : 'group'}
          onClick={handleToggleShowAll}
        >
          {showAll ? 'My Fund Requests' : 'Show All'}
        </Button>
      </Box>
      <FundingRequestTable
        fundingRequests={fundingRequests}
        loading={loading}
        onRefresh={() => loadFundingRequests(showAll, daysFilter)}
        onViewRequest={handleViewRequest}
        daysFilter={showAll ? daysFilter : null}
        onDaysFilterChange={handleDaysFilterChange}
      />
    </SpaceBetween>
  );
};

export default FundingRequestTracker;
